<template>

  <rate-calendar-popup :title="title">

    <template v-slot:tools>

      <v-btn-group>
        <v-btn icon="$edit" :title="$t('actions.edit_default_rates')" @click="$emit('edit')" />
        <v-btn icon="$delete" :title="$t('actions.delete_spot_rate')" @click="$emit('delete')" />
      </v-btn-group>

    </template>

    <table class="calendar-popup-table">
      <thead>
        <tr>
          <th scope="col">{{ $t('titles.default_cost') }}</th>
          <th v-if="hasChannelOverrides" scope="col">{{ $t('titles.channel') }}</th>
          <th v-if="hasChannelOverrides" scope="col">{{ $t('titles.channel_override') }}</th>
          <th scope="col">{{ $t('titles.spot_override') }}</th>
        </tr>
      </thead>
      <tbody>
        <template v-for="dr in dataForDisplay">
          <tr v-for="(cdr, i) in dr.channelDefaultRates.length ? dr.channelDefaultRates : [null]">
            <td v-if="i === 0" :rowspan="Math.max(1, dr.channelDefaultRates.length)">
              {{ currencyFormatter.format(dr.defaultCost) }}
              <template v-if="tieredRates">
                for
                {{ dr.perQuantity }}
                {{ dr.perUnit }}{{ dr.perQuantity !== null && dr.perQuantity > 1 ? 's' : '' }}
              </template>
            </td>
            <td v-if="hasChannelOverrides">
              <template v-if="cdr">
                {{ ChannelRegistry.findTitle(cdr.channel) }}
              </template>
            </td>
            <td v-if="hasChannelOverrides" class="text-right">
              <template v-if="cdr && cdr.channelOverride !== null">
                {{ currencyFormatter.format(cdr.channelOverride) }}
              </template>
              <template v-else>
                —
              </template>
            </td>
            <td class="text-right">
              <template v-if="cdr && cdr.spotOverride !== null">
                {{ currencyFormatter.format(cdr.spotOverride) }}
              </template>
              <template v-else>
                —
              </template>
            </td>
          </tr>
        </template>
      </tbody>
    </table>

  </rate-calendar-popup>

</template>

<script setup lang="ts">

import { computed } from 'vue'

// components
import RateCalendarPopup from './RateCalendarPopup.vue'

// models
import { DefaultRate } from '../../../../../models/DefaultRate'

// misc
import ChannelRegistry from '../../../../../channels/ChannelRegistry'
import VerticalRegistry from '../../../../../verticals/VerticalRegistry'
import { currencyFormatter } from '../../../../../utils/formatters'
import type { NonEventExceptionItem } from '../types'

interface Props {
  item: NonEventExceptionItem
}

const props = defineProps<Props>()

const $emit = defineEmits(['edit', 'delete'])

const title = computed<string>(() => {
  return props.item.locationProduct.product.name
})

const sortedDefaultRates = computed<DefaultRate[]>(() => {
  return props.item.defaultRates.sort((dr1: DefaultRate, dr2: DefaultRate) => {
    const duration1 = dr1.getDurationInHours()
    const duration2 = dr2.getDurationInHours()
    if (duration1 === null || duration2 === null) {
      return 0
    }
    return duration1 - duration2
  })
})

interface DisplayDefaultRate {
  defaultCost: number
  perQuantity: null | number
  perUnit: null | string
  channelDefaultRates: {
    channel: string
    channelOverride: null | number
    spotOverride: null | number
  }[]
}

const dataForDisplay = computed<DisplayDefaultRate[]>(() => {

  const output: DisplayDefaultRate[] = []

  sortedDefaultRates.value.forEach(dr => {

    output.push({
      defaultCost: dr.defaultCost,
      perQuantity: dr.perQuantity,
      perUnit: dr.perUnit,
      channelDefaultRates: dr.channelDefaultRates.map(cdr => {
        const sr = props.item.spotRates.find(sr => sr.defaultRateID === dr.ID && sr.channelFacilityID === cdr.channelFacilitiesID)
        return {
          channel: cdr.channel,
          channelOverride: cdr.channelCost !== dr.defaultCost ? cdr.channelCost : null,
          spotOverride: sr?.spotCost ?? null,
        }
      })
    })

  })

  return output

})

const hasChannelOverrides = computed<boolean>(() => {
  return dataForDisplay.value.some(dr => {
    return dr.channelDefaultRates.some(cdr => cdr.channelOverride !== null)
  })
})

const tieredRates = computed<boolean>(() => {
  return VerticalRegistry.get(props.item.locationProduct?.product.verticalType)?.supports(['tieredRates']) ?? false
})

</script>
